import React, { useEffect, useState } from 'react'
import TableView from '../shared/TableView'
import ExportMenu from './export_menu/ExportMenu'
import SaveMenu from './save_menu/SaveMenu'
import SavedSelections from './../../account/saved_selection/SavedSelections'
import ClearAll from '../shared/components/ClearAll'
import ClearAllSecondary from '../shared/components/ClearAllSecondary'
import sortData from '../utils/sorting'
import filterData from '../utils/filter'
import config from '../../../config/config'
import PasteIdInput from './PasteIdInput'
import Menu from './../data_select/menu/Menu'
import getUniqueCategories from '../../visualizations/single_visualization/utils/getUniqueCategories'

// Default filter object based on the structure used in Menu.js
const defaultFilterObject = [
  {
    selected: '',
    input: [{ var: '', filterRange: 'from', custom: true }],
    filterType: '',
    operator: 'and',
  },
]

// Default sort object (copied from Menu.js, assuming primary key is available via config)
const defaultSortObject = [{ selected: config.data.primary_key, order: 'asc' }]

// Function based component that displays the selected datapoints
function SelectedData({ selectedData, allData, dataType, updateSelection, tableUtils, setTableUtils }) {
  // Local sort state of the selected table, initialized to the primary key sort
  const [sorters, setSorters] = useState(defaultSortObject) // Initialize with defaultSortObject
  // Local filter state of the selected table
  const [filters, setFilters] = useState(defaultFilterObject)
  // State to track whether the component is expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(false)
  // State to manage the visibility of the paste identifier input area
  const [showPasteInput, setShowPasteInput] = useState(false);
  // State to manage the visibility of the saved selections area
  const [showSavedSelections, setShowSavedSelections] = useState(false);

  // Apply local filters first, then local sorting
  const filteredData = filterData(selectedData, filters)
  const updatedData = sortData(filteredData, [...sorters])

  const dataType_name = dataType === "main" ? config.data.name : config.data.secondary_name

  const [categories, setCategories] = useState(null)
    useEffect(() => {
      setCategories(getUniqueCategories(selectedData))
    }, [selectedData])

  // NEW FUNCTION: Clears the local filters and sorts of this selected data table
  const clearLocalFilterSort = () => {
    setFilters(defaultFilterObject)
    setSorters(defaultSortObject)
  }
  
  // Logic to determine if filters or sort are active
  const hasActiveLocalFilter = filters.some(f => f.selected)
  const hasActiveLocalSort = sorters.some(s => s.selected !== config.data.primary_key || s.order !== 'asc')

  // Toggle expanded/collapsed state for the main container
  const toggleExpand = () => {
    const newState = !isExpanded;
    setIsExpanded(newState)

    // Reset internal sub-views and local filters/sorters when collapsing
    if (!newState) {
      setShowPasteInput(false);
      setShowSavedSelections(false);
      clearLocalFilterSort(); // Reset local filters and sorters
    }
  }

  // Toggle paste identifier input visibility
  const togglePasteInput = () => {
    setShowPasteInput(!showPasteInput);
    if (!isExpanded) {
      setIsExpanded(true);
    }
    setShowSavedSelections(false);
  }

  // Toggle saved selections visibility
  const toggleSavedSelections = () => {
    setShowSavedSelections(!showSavedSelections);
    if (!isExpanded) {
      setIsExpanded(true);
    }
    setShowPasteInput(false);
  }
  
  // NOTE: This menuUpdate is kept but not used by the local Menu component, 
  // as its purpose is to update the GLOBAL context, which we want to avoid here.
  const menuUpdate = (filters, sorters) => {
    setTableUtils({
      ...tableUtils,
      filters: filters,
      sorters: sorters,
    })
  }


  return (
    <div
      tour="step-data-selected"
      className={`med-content-container selected-data-container ${isExpanded ? 'expanded-container' : ''}`}
    >
      <div>
        <div className="header-container">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button className="med-primary-solid med-bx-button" onClick={toggleExpand}>
              <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
              {isExpanded ? 'Hide selection' : 'Show selection'}
            </button>

            <h1 className="med-header" style={{ marginLeft: '10px' }}>
              Selected {dataType_name}
              {/* Conditional display logic for the count */}
              {updatedData.length === 0 && (
                <span className="selected-data-count"> - 0 - load, paste or select below</span>
              )}
              {updatedData.length > 0 && hasActiveLocalFilter && (
                <span className="selected-data-count"> - {updatedData.length} out of {selectedData.length} in selection displayed</span>
              )}
              {updatedData.length > 0 && !hasActiveLocalFilter && (
                <span className="selected-data-count"> - {updatedData.length} in selection</span>
              )}
            </h1>
          </div>

          {updatedData.length > 0 && (
            <div className="button-container">
              <ExportMenu selectedData={updatedData} text="Export Selection" />

              {dataType === "main" ? (
                <ClearAll data={updatedData} />
              ) : (
                <ClearAllSecondary data={updatedData} />
              )}

              <button
                tour="step-nav-vis"
                className="med-bx-button med-data-button med-primary-solid"
                onClick={() => (window.location.href = config.routes.main.visualizations)}
              >
                <i className="bx bx-bar-chart med-button-image"></i> Visualise Selection
              </button>
            </div>
          )}

          {updatedData.length === 0 && (
            <div className="button-container">
              <button
                className="med-bx-button med-data-button med-primary-solid"
                onClick={togglePasteInput}
              >
                <i className="bx bx-list-ul med-button-image"></i> {showPasteInput ? 'Hide Quick Select' : 'Quick Select'}
              </button>
              <button
                className="med-bx-button med-data-button med-primary-solid"
                onClick={toggleSavedSelections}
              >
                <i className="bx bx-folder-open med-button-image"></i> {showSavedSelections ? 'Hide saved' : 'Load saved'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 1. PASTE ID INPUT VIEW */}
      {isExpanded && showPasteInput && (
        <PasteIdInput
          allData={allData}
          updateSelection={updateSelection}
          config={config}
          togglePasteInput={togglePasteInput}
          selectedData={updatedData}
          tableUtils={tableUtils}
          setTableUtils={setTableUtils}
        />
      )}

      {/* 2. TABLE VIEW (Current Selection) */}
      {isExpanded && updatedData.length > 0 && !showSavedSelections && (
        <div className="selected-data-content">
          {!showPasteInput && (
            <>
              <hr className="med-top-separator" />
              <div className='med-action-button-group'>
                <button
                  className="med-bx-button med-data-button med-primary-solid"
                  onClick={togglePasteInput}
                >
                  <i className="bx bx-list-ul med-button-image"></i> Quick Select
                </button>
                <button
                  className="med-bx-button med-data-button med-primary-solid"
                  onClick={toggleSavedSelections}
                >
                  <i className="bx bx-folder-open med-button-image"></i> Load saved
                </button>
                <SaveMenu selectedData={updatedData} text="Save Selection" />

                {dataType === "main" ? (
                  <ClearAll data={updatedData} />
                ) : (
                  <ClearAllSecondary data={updatedData} />
                )}
                <Menu
                  filters={filters}
                  sorters={sorters}
                  update={(newFilters, newSorters) => { // Updates LOCAL state
                    setFilters(newFilters)
                    setSorters(newSorters)
                  }}
                  categories={categories}
                />
                {/* Clear Filter/Sort Button, shown only when active */}
                {(hasActiveLocalFilter || hasActiveLocalSort) && (
                  <button
                    className="med-bx-button med-data-button med-primary-solid"
                    onClick={clearLocalFilterSort}
                    style={{
                      width: 'auto',
                    }}
                  >
                    <i className="bx bx-x med-button-image" ></i>
                    Clear Filter/Sort
                  </button>
                )}
              </div>
            </>
          )}

          <TableView
            data={updatedData}
            sorters={sorters} // Use local sorters
            setSorters={setSorters} // Set local sorters
            select={false}
            text="No data selected. Select data points from the table below or load a previously exported EMRD dataset."
            dataType={dataType}
            pageSize={5}
            style={showPasteInput ? { marginTop: '10px' } : {}}
          />
        </div>
      )}


      {/* 3. SAVED SELECTIONS VIEW */}
      {isExpanded && showSavedSelections && (
        <div className="selected-data-content">
          <hr className="med-top-separator" />
          {updatedData.length > 0 && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <button
                className="med-bx-button med-data-button med-primary-solid"
                onClick={togglePasteInput}
              >
                <i className="bx bx-list-ul med-button-image"></i> Quick Select
              </button>
              <button
                className="med-bx-button med-data-button med-primary-solid"
                onClick={toggleSavedSelections}
              >
                <i className="bx bx-folder-open med-button-image"></i> Hide saved
              </button>
            </div>
          )}
          <h2 className="med-header">Saved Selections</h2>
          <SavedSelections />
        </div>
      )}

      {/* 4. DEFAULT VIEW */}
      {isExpanded && updatedData.length === 0 && !showPasteInput && !showSavedSelections && (
        <div className="selected-data-content">
        </div>
      )}
    </div>
  )
}

export default SelectedData